import { capitalize } from "lodash-es";
import { AlertCircle } from "lucide-react";
import { useMemo } from "react";

import { LoadingScreen } from "@/components/loading-screen";
import { SearchBar } from "@/components/search-bar";
import { useTableControl } from "@/components/table-control";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { Badge } from "@/components/ui/badge";
import { OnboardingHeader } from "@/features/registration/components/onboarding-header";
import { useOrganisation } from "@/features/select-organisation/api/get-my-organisation";
import { useGetInvitations } from "@/features/users/api/get-invitations";
import { AddInvitationModal } from "@/features/users/components/add-invitation-modal";
import { InvitationRowActions } from "@/features/users/components/invitation-row-actions";
import { UsersTable } from "@/features/users/components/users-table";
import {
  InviteStatusEnum,
  PublicInvitation,
  RoleGroupEnum,
} from "@classview/shared-types";
import { createColumnHelper } from "@tanstack/react-table";

export function InvitationsPage() {
  const columnHelper = createColumnHelper<PublicInvitation>();
  const { pagination, setPagination, search, setSearch } = useTableControl();
  const { organisation } = useOrganisation();
  const {
    data: invitationsData,
    isLoading,
    isSuccess,
    isError,
    isFetching,
    isPlaceholderData,
    error,
  } = useGetInvitations({
    pageSize: pagination.pageSize,
    pageIndex: pagination.pageIndex,
    search,
  });

  const columns = useMemo(
    () => [
      columnHelper.accessor("email", {
        id: "email",
        cell: (info) => info.getValue(),
        header: () => <div className="truncate">Email</div>,
        enableSorting: false,
        size: 999,
      }),
      columnHelper.display({
        id: "role",
        cell: (info) => {
          const rolesPerOrganisation = info.row.original.organisations;
          const role = rolesPerOrganisation[organisation.id]?.role;

          if (role === RoleGroupEnum.SESSION_GUEST) {
            return "Guest";
          }

          return role ? capitalize(role) : "No role";
        },
        header: () => <span>Role</span>,
        enableSorting: false,
        size: 100,
      }),
      columnHelper.display({
        id: "status",
        cell: (info) => {
          const status = info.row.original.status;

          return (
            <Badge variant="outline">
              {(() => {
                switch (status) {
                  case "PENDING":
                    return (
                      <p className="flex items-center">
                        <span className="mr-2 inline-block size-2 rounded-full bg-warning" />
                        Pending
                      </p>
                    );
                  case "ACCEPTED":
                    return (
                      <p className="flex items-center">
                        <span className="mr-2 inline-block size-2 rounded-full bg-green-500" />
                        {capitalize(status)}
                      </p>
                    );
                  default:
                    return (
                      <p className="flex items-center">
                        <span className="mr-2 inline-block size-2 rounded-full bg-red-500" />
                        {capitalize(status)}
                      </p>
                    );
                }
              })()}
            </Badge>
          );
        },
        header: () => <span>Status</span>,
        enableSorting: false,
        size: 100,
      }),
      columnHelper.display({
        id: "actions",
        header: () => <span className="sr-only">Actions</span>,
        cell: (info) => {
          if (info.row.original.status === InviteStatusEnum.ACCEPTED) {
            return null;
          }
          return <InvitationRowActions invitationId={info.row.original.id} />;
        },
        size: 70,
      }),
    ],
    [organisation.id, columnHelper],
  );

  if (isLoading)
    return (
      <div>
        <OnboardingHeader
          title="Manage Invitations"
          description="View and manage invitations"
          className="mb-6"
        />
        <LoadingScreen />
      </div>
    );

  if (isError || !isSuccess)
    return (
      <div>
        <OnboardingHeader
          title="Manage Invitations"
          description="View and manage invitations"
          className="mb-6"
        />
        <Alert variant="destructive">
          <AlertCircle className="h-4 w-4" />
          <AlertTitle>Error</AlertTitle>
          <AlertDescription>
            {error?.message ?? "An unexpected error occurred"}
          </AlertDescription>
        </Alert>
      </div>
    );

  return (
    <div>
      <OnboardingHeader
        title="Manage Invitations"
        description="View and manage invitations"
        className="mb-6"
      />
      <div className="mb-2 justify-between sm:flex">
        <SearchBar
          search={search}
          setSearch={setSearch}
          className="mb-2 sm:mb-0 sm:max-w-xs"
        />

        <AddInvitationModal organisationId={organisation.id} />
      </div>
      <UsersTable
        columns={columns}
        data={invitationsData.items}
        pagination={pagination}
        pageCount={Math.ceil(invitationsData.count / pagination.pageSize)}
        onPaginationChange={setPagination}
        isLoading={isFetching && isPlaceholderData}
      />
    </div>
  );
}
